import { Link, useNavigate } from "react-router-dom";
import { Paper, Title, Text, Loader, Center, Button, Container, Divider, Group, ScrollArea, Space, Stack, ThemeIcon, Menu, SimpleGrid, Progress, useMantineTheme, ActionIcon, Badge } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconPencil, IconDots, IconTrash, IconUsers, IconRotate2, IconChevronLeft, IconUser } from "@tabler/icons-react";
import moment from "moment";

import { ListWorkOrderDto } from "../../models/workorder.models";
import { TeamDto } from "../../models/team.models";
import { TeamWorkOrderList } from "./TeamWorkOrderList";
import { useAccess } from "../../Auth/auth-functions";
import { AzureADRoles } from "../../Auth/auth-roles";
import { CrudUserDate } from "../Shared/CrudUserDate";
import classes from './TeamDetailView.module.css';

interface TeamDetailViewProps {
	team?: TeamDto | null;
	isLoading?: boolean;
	onEdit?: () => void;
	onDelete?: (team: TeamDto) => void;
	onRestore?: (team?: TeamDto) => void;
	isRestoring?: boolean;
	onClose?: () => void;
}

export const TeamDetailView = ({ team, isLoading, onEdit, onDelete, onRestore, isRestoring, onClose }: TeamDetailViewProps) => {
	const navigate = useNavigate();
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`, true);
	const canEditTeam = useAccess(AzureADRoles.Admin);

	const percentageCompleted = team && team.WorkOrdersAssigned > 0 ? Math.round(team.WorkOrdersCompleted / team.WorkOrdersAssigned * 100) : 0;

	const handleWorkOrderSelected = (workOrder: ListWorkOrderDto): void => {
		navigate(`/workorders/${workOrder.WorkOrderId}`);
	};

	const deleteTeam = () => {
		if (!team) return;

		if (onDelete && team)
			onDelete(team);
	}

	const restoreTeam = () => {
		if (!team) return;

		if (onRestore && team)
			onRestore(team);
	}

	if (isLoading) return <Paper withBorder={!isMobile} p={0} radius={isMobile ? "0" : "md"} style={{ overflow: 'hidden' }}>
			<ScrollArea type={isMobile ? "never" : "scroll"} style={{ height: isMobile ? 'unset' : 'calc(100vh - 160px)' }}>
				<Center>
					<Loader size="sm" mt="lg" />
				</Center>
			</ScrollArea>
		</Paper>
	if (!team) return <Paper withBorder={!isMobile} p={0} radius={isMobile ? "0" : "md"} style={{ overflow: 'hidden' }}>
			<ScrollArea type={isMobile ? "never" : "scroll"} style={{ height: isMobile ? 'unset' : 'calc(100vh - 160px)' }}>
				<Space h="md" />
				<Stack justify="center" align="center" gap="md" pt="lg">
					<ThemeIcon size={100} radius={100} variant="light" color="gray">
						<IconUsers size={60} />
					</ThemeIcon>
					<Text fz="lg" fw="500" c="dimmed">Select a team to view details</Text>
				</Stack>
				<Space h="md" />
			</ScrollArea>
		</Paper>

	return <>
		<Paper withBorder={!isMobile} p={0} radius={isMobile ? "0" : "md"} style={{ overflow: 'hidden' }}>
			<ScrollArea type={isMobile ? "never" : "hover"} style={{ height: isMobile ? 'calc(100vh)' : 'calc(100vh - 160px)' }}>
				<Stack gap={0} mb="md" justify="center" className={classes['header']}>
					<Group justify="space-between" px="20" wrap="nowrap">
						<Group gap="xs" wrap="nowrap">
							<ActionIcon variant="light" radius="xl" onClick={onClose} hiddenFrom="sm">
								<IconChevronLeft />
							</ActionIcon>
							<Stack gap={0}>
								<Title order={isMobile ? 6 : 3}>{team?.Name}</Title>
								<Text fz="xs" c="dimmed">Created on {moment(team?.Created).format("MMMM D, YYYY")}</Text>
							</Stack>
						</Group>
						<Group gap="xs">
							<Button.Group mr={-10}>
								{team.Deleted == null && <>
								{!isMobile &&
								<Button onClick={onEdit} disabled={!canEditTeam} variant="subtle" radius="xl" leftSection={<IconPencil stroke={1.5} />}>Edit</Button>
								}
								{isMobile &&
								<Button onClick={onEdit} disabled={!canEditTeam} variant="subtle" radius="xl" px="sm"><IconPencil stroke={1.5} /></Button>
								}
								<Divider orientation="vertical" />
								<Menu position="bottom-end" shadow="md" radius="lg">
									<Menu.Target>
										<Button disabled={!canEditTeam} variant="subtle" radius="xl" px="sm"><IconDots size={20} /></Button>
									</Menu.Target>
									<Menu.Dropdown>
										<Menu.Item onClick={deleteTeam} color="red" leftSection={<IconTrash stroke={1.5} />} fw="bold">Delete</Menu.Item>
									</Menu.Dropdown>
								</Menu>
								</>}
								{team.Deleted != null && (
									<Button onClick={restoreTeam} loading={isRestoring} variant="outline" radius="lg" leftSection={<IconRotate2 stroke={1.5} />}>Restore</Button>
								)}
							</Button.Group>
						</Group>
					</Group>
				</Stack>

				{team.Deleted && <>
					<Container px="20" mt={40}>
						<Stack align="center">
							<ThemeIcon size={90} radius={60} variant="light" color="gray">
								<IconTrash size={60} />
							</ThemeIcon>
							<Stack align="center" gap={0}>
								<Text fz="xl">Team Deleted</Text>
								<Text fz="xs" c="dimmed">Deleted on {moment(team?.Deleted).format("MMMM D, YYYY")}</Text>
							</Stack>
						</Stack>
					</Container>
				</>}

				<Container px="20" hidden={team.Deleted != null}>
					{/* <Space h="md" /> */}
					<Text fz="sm" fw="bold">Description</Text>
					<Space h="xs" />
					<Text fz="sm">{team?.Description}</Text>
					<Space h="md" />

					
					<SimpleGrid cols={2}>

					{true && <Stack gap="xs" pb="md">
					<Text fz="sm" fw="bold">Work Orders Assigned</Text>
					<Text fz="sm">{team?.WorkOrdersAssigned}</Text>
					</Stack>}
					
					{true && <Stack gap="xs">
					<Text fz="sm" fw="bold">{percentageCompleted}% Completed</Text>
					<Progress value={percentageCompleted} size="md" mt="xs" radius="xl" />
					</Stack>}
					</SimpleGrid>

					{team?.Members.length > 0 && <>
						<Text fz="sm" fw="bold">Members</Text>
						<Space h="xs" />
						<Group wrap="wrap" gap="xs">
							{team.Members.map(member => (
								<Link key={member.UserId} to={{ pathname: `/users/${member.UserId}` }} className={classes['link']}>
									<Badge size="lg" fz="sm" radius="sm" leftSection={<IconUser size={14} />}>{member.Name}</Badge>
								</Link>
							))}
						</Group>
						<Divider my="md" />
					</>}

					<SimpleGrid cols={isMobile ? 1 : 2}>
						<CrudUserDate label="Created" userId={team.CreatedBy.UserId} userName={team.CreatedBy.Name} date={team.Created} />
						{team.UpdatedBy && !team.Deleted && <>
							<CrudUserDate label="Updated" userId={team.UpdatedBy.UserId} userName={team.UpdatedBy.Name} date={team.Updated} />
						</>}
						{team.DeletedBy && <>
							<CrudUserDate label="Deleted" userId={team.DeletedBy.UserId} userName={team.DeletedBy.Name} date={team.Deleted} />
						</>}
					</SimpleGrid>
					<Divider my="md" />

					<Text fz="sm" fw="bold" mt="md">Work Order History</Text>
					<Space h="md" />
					<TeamWorkOrderList teamId={team.TeamId} onSelect={handleWorkOrderSelected} />
					<Space h="md" />
				</Container>
			</ScrollArea>
		</Paper>
	</>;
}
